// Demo-mode bootstrap. When active, replaces window.fetch and window.WebSocket
// with lightweight shims that serve the synthetic demo fleet (demoFleet.js) so a
// fully static build of the PWA renders a live, animating fleet with NO backend
// — no API, no database, no telemetry pipeline. Also injects an on-screen
// control panel to scrub the simulated time of day and change how fast time runs.
//
// Activated when the build sets VITE_DEMO=1, or for ad-hoc testing against a
// normal dev build via a `?demo=1` query param. Only `/api/*` requests are
// intercepted; map tiles (Esri CDN), fonts, and app assets pass straight through.

import {
  computeFleet, computeWsSnapshot, computeLiveReport, computeSharedBanks, computeRunway, writeRunway,
  DEMO_EVENT, DEMO_EVENTS, DEMO_CAPABILITIES,
  getSimHour, setSimHour, getSpeed, setSpeed,
} from './demoFleet.js';
import { PRIMARY_EVENT_STORAGE_KEY } from '../fleet-map/constants.js';

export function isDemo() {
  // Build-time flag ONLY. Intentionally NOT gated on a `?demo=1` query param so
  // the production client portal can never be flipped to synthetic data via a
  // URL — demo mode exists only in the dedicated VITE_DEMO=1 build (dist-demo).
  // To test against a dev server: `VITE_DEMO=1 npm run dev`.
  try {
    return !!(import.meta.env && import.meta.env.VITE_DEMO === '1');
  } catch {
    return false;
  }
}

function jsonResponse(obj, status = 200) {
  return new Response(JSON.stringify(obj), {
    status,
    headers: { 'Content-Type': 'application/json' },
  });
}

/** The decoded path segment after `/<kind>/`, or null when the path has none. */
function pathId(path, kind) {
  const m = path.match(new RegExp(`/${kind}/([^/]+)`));
  return m ? decodeURIComponent(m[1]) : null;
}

/** The demo event a /events/<id> path names. An unknown id answers the demo
 * site's event, the way every id did before the second event existed. */
function eventByPath(path) {
  const id = pathId(path, 'events');
  return DEMO_EVENTS.find((e) => e.event_id === id) || DEMO_EVENT;
}

function readBody(body) {
  if (typeof body !== 'string' || !body) return {};
  try { return JSON.parse(body); } catch { return {}; }
}

function routeApi(path, method, body) {
  if (path.endsWith('/api/capabilities')) return jsonResponse(DEMO_CAPABILITIES);
  // Generator runway advisor (plan 2026-10-17-2154, U10). Ahead of the
  // /events branches, which would read /runway/events/<id> as an event.
  if (path.includes('/api/runway/')) {
    if (method === 'GET') return jsonResponse(computeRunway(pathId(path, 'events') || DEMO_EVENT.window_id));
    const res = writeRunway(path, method, readBody(body));
    return jsonResponse(res.body, res.status);
  }
  // Fleet table view templates (plan 2026-09-26-0019). No demo caller is an
  // employee, so the settings route answers the way it answers a portal user
  // and the table keeps its layouts in the browser. The bank list is the one
  // demo bank, and bank writes succeed without changing it.
  if (path.endsWith('/api/table-settings')) {
    return jsonResponse({ error: 'Employee access required' }, 401);
  }
  if (path.includes('/shared-banks')) {
    if (method !== 'GET') return jsonResponse({ ok: true });
    return jsonResponse(computeSharedBanks(undefined, pathId(path, 'windows') || DEMO_EVENT.window_id));
  }
  if (path.includes('/deployments')) return jsonResponse({ deployments: DEMO_CAPABILITIES.deployments });
  if (path.includes('/nodes')) {
    return jsonResponse({ nodes: computeFleet(), vrm_enabled: false, vrm_nodes_added: 0 });
  }
  if (path.includes('/events/registry')) return jsonResponse({ events: [] });
  // The New Event box's job list (plan 2026-09-21-2135, U7). Made-up jobs,
  // never real ones: the live list names every customer.
  if (path.endsWith('/api/jobs')) {
    return jsonResponse({
      jobs: [
        { slug: 'demo-2026-10-harbor-lights', canonical_name: 'DMO_2026-10_harbor-lights-festival', created_at: '2026-10-01T17:00:00+00:00', events: [] },
        { slug: 'demo-2026-09-live-demo', canonical_name: 'DMO_2026-09_live-demo-site', created_at: '2026-09-01T17:00:00+00:00', events: [{ window_id: DEMO_EVENT.window_id, name: DEMO_EVENT.event_name, open: true }] },
        { slug: 'demo-2026-08-valley-fair', canonical_name: 'DMO_2026-08_valley-county-fair', created_at: '2026-08-01T17:00:00+00:00', events: [{ window_id: 'demo-ended-fair', name: 'DMO_2026-08_valley-county-fair', open: false }] },
      ],
      unavailable: false,
    });
  }
  if (method === 'POST' && path.includes('/events/create')) return jsonResponse(DEMO_EVENT);
  if (/\/events\/[^/]+(\/)?$/.test(path) && !path.endsWith('/events')) {
    return jsonResponse({ ...eventByPath(path), memberships: [] });
  }
  if (path.includes('/events')) return jsonResponse({ events: DEMO_EVENTS });
  if (path.includes('/reports/live/')) {
    return jsonResponse(computeLiveReport(undefined, pathId(path, 'live') || DEMO_EVENT.event_id));
  }
  if (path.includes('/reports/energy/')) return jsonResponse({ summary: {}, daily: [], pv_summary: {}, has_pv: true });
  if (path.includes('/insights/')) return jsonResponse({ generated_at: new Date().toISOString(), payload: { per_system: {} } });
  if (path.includes('/alarms')) return jsonResponse({ alarms: [] });
  if (path.includes('/mqtt-token')) return jsonResponse({});
  if (path.includes('/peers/realtime')) return jsonResponse({ local: null, peers: [], ts: Date.now() });
  return method === 'GET' ? jsonResponse({}) : jsonResponse({ ok: true });
}

function installFetch() {
  const orig = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const rawUrl = typeof input === 'string' ? input : (input && input.url) || '';
    let path = rawUrl;
    try { path = new URL(rawUrl, window.location.origin).pathname; } catch { /* keep raw */ }
    if (!path.includes('/api/')) return orig(input, init);
    const method = ((init && init.method) || (typeof input === 'object' && input.method) || 'GET').toUpperCase();
    try {
      return routeApi(path, method, init && init.body);
    } catch (err) {
      console.warn('[demo] route error', path, err);
      return jsonResponse({});
    }
  };
}

function installWebSocket() {
  const SNAPSHOT_MS = 1000;
  class DemoWebSocket {
    constructor(url) {
      this.url = String(url);
      this.readyState = 0;
      this.onopen = null;
      this.onmessage = null;
      this.onclose = null;
      this.onerror = null;
      this._timer = null;
      const isRealtime = this.url.includes('/ws/realtime');
      setTimeout(() => {
        if (!isRealtime) {
          this.readyState = 3;
          this.onclose && this.onclose({});
          return;
        }
        this.readyState = 1;
        this.onopen && this.onopen({});
        this._tick();
      }, 80);
    }
    _tick() {
      if (this.readyState !== 1) return;
      try {
        this.onmessage && this.onmessage({ data: JSON.stringify(computeWsSnapshot()) });
      } catch (err) {
        console.warn('[demo] ws tick error', err);
      }
      this._timer = setTimeout(() => this._tick(), SNAPSHOT_MS);
    }
    send() {}
    close() {
      this.readyState = 3;
      if (this._timer) clearTimeout(this._timer);
      this.onclose && this.onclose({});
    }
    addEventListener(type, fn) { this['on' + type] = fn; }
    removeEventListener(type) { this['on' + type] = null; }
  }
  DemoWebSocket.CONNECTING = 0;
  DemoWebSocket.OPEN = 1;
  DemoWebSocket.CLOSING = 2;
  DemoWebSocket.CLOSED = 3;
  window.WebSocket = DemoWebSocket;
}

// ── on-screen control panel (time-of-day + speed) ─────────────────────────
function fmtHour(h) {
  const hh = Math.floor(h) % 24;
  const mm = Math.floor((h - Math.floor(h)) * 60);
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}
function fmtSpeed(s) {
  if (s === 0) return 'Paused';
  const secsPerDay = (4 * 60 * 1000) / 1000 / s; // DAY_MS is 4 min
  const perDay = secsPerDay >= 120 ? `${(secsPerDay / 60).toFixed(1)} min/day` : `${Math.round(secsPerDay)} s/day`;
  const label = s % 1 === 0 ? `${s}×` : `${s.toFixed(1)}×`;
  return `${label} · ${perDay}`;
}

function installControls() {
  const mount = () => {
    if (document.getElementById('ovr-demo-controls')) return;
    const el = document.createElement('div');
    el.id = 'ovr-demo-controls';
    el.innerHTML = `
      <style>
        #ovr-demo-controls{position:fixed;left:50%;bottom:14px;transform:translateX(-50%);
          z-index:10000;background:rgba(15,23,42,.86);backdrop-filter:blur(8px);
          color:#e2e8f0;border:1px solid rgba(148,163,184,.25);border-radius:12px;
          padding:10px 14px;font:12px/1.3 system-ui,-apple-system,sans-serif;
          box-shadow:0 8px 30px rgba(0,0,0,.4);display:flex;align-items:center;gap:16px;
          max-width:min(680px,calc(100vw - 24px));flex-wrap:wrap;justify-content:center;}
        #ovr-demo-controls .ovr-badge{display:flex;align-items:center;gap:6px;font-weight:600;
          letter-spacing:.02em;color:#7dd3fc;white-space:nowrap;}
        #ovr-demo-controls .ovr-badge .dot{width:7px;height:7px;border-radius:50%;background:#22c55e;
          box-shadow:0 0 8px #22c55e;animation:ovrpulse 2s infinite;}
        @keyframes ovrpulse{0%,100%{opacity:1}50%{opacity:.35}}
        #ovr-demo-controls .ovr-row{display:flex;align-items:center;gap:8px;}
        #ovr-demo-controls label{color:#94a3b8;white-space:nowrap;}
        #ovr-demo-controls input[type=range]{accent-color:#38bdf8;width:150px;cursor:pointer;}
        #ovr-demo-controls .ovr-val{font-variant-numeric:tabular-nums;min-width:96px;color:#f1f5f9;font-weight:600;}
        @media(max-width:560px){#ovr-demo-controls input[type=range]{width:120px;}}
      </style>
      <span class="ovr-badge"><span class="dot"></span>Live demo</span>
      <div class="ovr-row">
        <label for="ovr-time">Time</label>
        <input id="ovr-time" type="range" min="0" max="24" step="0.1" />
        <span class="ovr-val" id="ovr-time-val">--:--</span>
      </div>
      <div class="ovr-row">
        <label for="ovr-speed">Speed</label>
        <input id="ovr-speed" type="range" min="0" max="12" step="0.5" />
        <span class="ovr-val" id="ovr-speed-val">--</span>
      </div>
    `;
    document.body.appendChild(el);

    const timeEl = el.querySelector('#ovr-time');
    const timeVal = el.querySelector('#ovr-time-val');
    const speedEl = el.querySelector('#ovr-speed');
    const speedVal = el.querySelector('#ovr-speed-val');

    speedEl.value = String(getSpeed());
    speedVal.textContent = fmtSpeed(getSpeed());

    let dragging = false;
    const onDragStart = () => { dragging = true; };
    const onDragEnd = () => { setTimeout(() => { dragging = false; }, 200); };
    timeEl.addEventListener('pointerdown', onDragStart);
    timeEl.addEventListener('pointerup', onDragEnd);
    timeEl.addEventListener('input', () => {
      const h = parseFloat(timeEl.value);
      setSimHour(h);
      timeVal.textContent = fmtHour(h);
    });
    speedEl.addEventListener('input', () => {
      const s = parseFloat(speedEl.value);
      setSpeed(s);
      speedVal.textContent = fmtSpeed(s);
    });

    // Keep the time slider + readout in sync with the running clock.
    setInterval(() => {
      if (!dragging) {
        const h = getSimHour();
        timeEl.value = String(h);
        timeVal.textContent = fmtHour(h);
      }
    }, 250);
  };

  if (document.body) mount();
  else window.addEventListener('DOMContentLoaded', mount, { once: true });
}

// The demo serves two active events, so the app's auto-select of a sole
// active event no longer fires. Seed the primary event once, so the demo
// still opens on the demo site. A later pick (the HeadTrip layout) persists
// over this seed the way any pick does.
function seedPrimaryEvent() {
  try {
    if (localStorage.getItem(PRIMARY_EVENT_STORAGE_KEY)) return;
    localStorage.setItem(PRIMARY_EVENT_STORAGE_KEY, JSON.stringify({
      event_id: DEMO_EVENT.event_id, event_name: DEMO_EVENT.event_name,
    }));
  } catch { /* storage unavailable: the app opens with no event selected */ }
}

export function installDemo() {
  seedPrimaryEvent();
  installFetch();
  installWebSocket();
  installControls();
  // eslint-disable-next-line no-console
  console.info('%c[demo mode] synthetic fleet — no backend', 'color:#22c55e');
}
